/** Komponentë bazë të ndërfaqes (stil i ngjashëm me shadcn/ui, pa varësi shtesë). */
import clsx from 'clsx';
import React, { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

type Varianti = 'kryesor' | 'dytesor' | 'rrezik' | 'fantazme';

export const Buton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { varianti?: Varianti; ngarkohet?: boolean }>(
  ({ varianti = 'kryesor', ngarkohet, className, children, disabled, ...p }, ref) => (
    <button
      ref={ref}
      disabled={disabled || ngarkohet}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-marka-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        varianti === 'kryesor' && 'bg-marka-600 text-white hover:bg-marka-700',
        varianti === 'dytesor' && 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
        varianti === 'rrezik' && 'bg-red-600 text-white hover:bg-red-700',
        varianti === 'fantazme' && 'text-slate-600 hover:bg-slate-100',
        className,
      )}
      {...p}
    >
      {ngarkohet && <Rrotullues className="h-4 w-4" />}
      {children}
    </button>
  ),
);
Buton.displayName = 'Buton';

export const Hyrje = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <input
    ref={ref}
    className={clsx(
      'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-marka-500 focus:outline-none focus:ring-1 focus:ring-marka-500',
      className,
    )}
    {...p}
  />
));
Hyrje.displayName = 'Hyrje';

export function Zgjedhje({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={clsx(
        'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-marka-500 focus:outline-none focus:ring-1 focus:ring-marka-500',
        className,
      )}
      {...p}
    />
  );
}

export function Etikete({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
      {children}
    </label>
  );
}

export function Karte({ children, className, titulli, veprime }: { children: ReactNode; className?: string; titulli?: ReactNode; veprime?: ReactNode }) {
  return (
    <section className={clsx('rounded-lg border border-slate-200 bg-white shadow-sm', className)}>
      {(titulli || veprime) && (
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          {titulli && <h2 className="text-sm font-semibold text-slate-800">{titulli}</h2>}
          {veprime}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Rrotullues({ className }: { className?: string }) {
  return (
    <svg className={clsx('animate-spin text-current', className ?? 'h-5 w-5')} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" className="opacity-75" />
    </svg>
  );
}

export function Ngarkim({ teksti = 'Duke ngarkuar…' }: { teksti?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-slate-500" role="status">
      <Rrotullues /> {teksti}
    </div>
  );
}

export function Bosh({ titulli, pershkrimi, veprim }: { titulli: string; pershkrimi?: string; veprim?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 px-6 py-12 text-center">
      <p className="text-sm font-medium text-slate-700">{titulli}</p>
      {pershkrimi && <p className="mt-1 text-sm text-slate-500">{pershkrimi}</p>}
      {veprim && <div className="mt-4">{veprim}</div>}
    </div>
  );
}

export function Gabim({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{children}</div>;
}

export function Njoftim({ children }: { children: ReactNode }) {
  return <div className="rounded-md border border-marka-100 bg-marka-50 px-3 py-2 text-sm text-marka-900">{children}</div>;
}

export function Tabele({ koka, children }: { koka: ReactNode[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            {koka.map((k, i) => (
              <th key={i} className="whitespace-nowrap px-3 py-2 font-medium">
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Kpi({ etiketa, vlera, ngjyra = 'slate' }: { etiketa: string; vlera: ReactNode; ngjyra?: 'slate' | 'green' | 'yellow' | 'orange' | 'red' | 'gray' }) {
  const shirit = { slate: 'bg-marka-600', green: 'bg-emerald-500', yellow: 'bg-amber-400', orange: 'bg-orange-500', red: 'bg-red-600', gray: 'bg-slate-400' }[ngjyra];
  return (
    <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <span className={clsx('absolute inset-y-0 left-0 w-1', shirit)} />
      <p className="text-xs font-medium text-slate-500">{etiketa}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{vlera}</p>
    </div>
  );
}

// ── F2: komponentë shtesë ────────────────────────────────────────────────────

export const TekstZone = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea
    ref={ref}
    className={clsx(
      'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:border-marka-500 focus:outline-none focus:ring-1 focus:ring-marka-500',
      className,
    )}
    rows={3}
    {...p}
  />
));
TekstZone.displayName = 'TekstZone';

/** Etiketë + fushë + gabim, në një rrjet formulari. */
export function Fushe({ etiketa, children, gjeresi, ndihme, detyrueshme }: { etiketa: string; children: ReactNode; gjeresi?: 'plote' | 'gjysme'; ndihme?: string; detyrueshme?: boolean }) {
  return (
    <div className={gjeresi === 'plote' ? 'sm:col-span-2' : ''}>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">
          {etiketa}
          {detyrueshme && <span className="text-red-600"> *</span>}
        </span>
        {children}
      </label>
      {ndihme && <p className="mt-1 text-xs text-slate-500">{ndihme}</p>}
    </div>
  );
}

/** Dritare modale e thjeshtë (mbyllet me Escape ose me klik jashtë). */
export function Modal({ hapur, mbyll, titulli, children, gjeresi = 'max-w-2xl' }: { hapur: boolean; mbyll: () => void; titulli: string; children: ReactNode; gjeresi?: string }) {
  if (!hapur) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10"
      onMouseDown={(e) => e.target === e.currentTarget && mbyll()}
      onKeyDown={(e) => e.key === 'Escape' && mbyll()}
      role="dialog"
      aria-modal="true"
      aria-label={titulli}
    >
      <div className={clsx('w-full rounded-lg bg-white shadow-xl', gjeresi)}>
        <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="text-base font-semibold">{titulli}</h2>
          <button onClick={mbyll} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Mbyll">
            ✕
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/** Skeda (tabs) të kontrolluara. */
export function Skeda<T extends string>({ skedat, aktive, ndrysho }: { skedat: Array<{ id: T; etiketa: string; numri?: number }>; aktive: T; ndrysho: (t: T) => void }) {
  return (
    <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
      {skedat.map((s) => (
        <button
          key={s.id}
          onClick={() => ndrysho(s.id)}
          className={clsx(
            '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium',
            aktive === s.id ? 'border-marka-600 text-marka-700' : 'border-transparent text-slate-500 hover:text-slate-800',
          )}
        >
          {s.etiketa}
          {s.numri !== undefined && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 text-xs text-slate-600">{s.numri}</span>}
        </button>
      ))}
    </div>
  );
}

/** Çift etiketë–vlerë për faqet e detajeve. */
export function Detaj({ etiketa, children }: { etiketa: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{etiketa}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{children || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

export function Etiketa({ children, ngjyra = 'slate' }: { children: ReactNode; ngjyra?: 'slate' | 'blue' | 'green' | 'amber' | 'red' }) {
  const s = {
    slate: 'bg-slate-100 text-slate-700',
    blue: 'bg-marka-50 text-marka-700',
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-700',
  }[ngjyra];
  return <span className={clsx('inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium', s)}>{children}</span>;
}
